"use client";

import { useEffect, useRef } from "react";
import { Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  author: "Jane D.",
  position: "Independent Filmmaker",
  quote: "Freedom Productions helped us streamline our operations and secure funding for our film project.",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1517061/img/testimonial_filmmaker_jane.png"
}, {
  id: 2,
  author: "Michael R.",
  position: "Small Business Owner",
  quote: "Their strategic guidance and project management made a huge difference in our business growth.",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1517061/img/testimonial_business_michael.png"
}, {
  id: 3,
  author: "Sarah L.",
  position: "Creative Producer",
  quote: "The team at Freedom Productions brought clarity and structure to our project development process.",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1517061/img/testimonial_producer_sarah.png"
}];
export default function Testimonials() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const api = useRef<CarouselApi | null>(null);
  const autoplayIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveredRef = useRef(false);
  useEffect(() => {
    if (!api.current) return;
    const startAutoplay = () => {
      if (autoplayIntervalRef.current) {
        clearInterval(autoplayIntervalRef.current);
      }
      autoplayIntervalRef.current = setInterval(() => {
        if (!isHoveredRef.current && api.current) {
          api.current.scrollNext();
        }
      }, 4000);
    };
    startAutoplay();
    return () => {
      if (autoplayIntervalRef.current) {
        clearInterval(autoplayIntervalRef.current);
      }
    };
  }, [api.current]);
  const handleMouseEnter = () => {
    isHoveredRef.current = true;
  };
  const handleMouseLeave = () => {
    isHoveredRef.current = false;
  };
  return <section ref={sectionRef} className="py-16 md:py-24 bg-background" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <div className={`text-center mb-12 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
            Client Testimonials
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
            Hear from clients who have experienced measurable growth and success through our professional support services.
          </p>
        </div>

        <Carousel setApi={apiInstance => {
        api.current = apiInstance;
      }} opts={{
        align: "center",
        loop: true
      }} orientation="vertical" className={`w-full transition-all duration-[var(--duration-medium)] delay-100 ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <CarouselContent className="h-[400px] md:h-[350px]">
            {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i} className="pt-4">
                <Card className="h-full border border-border shadow-soft hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1">
                  <CardContent className="flex flex-col items-center justify-center text-center p-8 md:p-10 h-full">
                    {testimonial.logo && <div className="mb-6 transition-transform duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:scale-105">
                        <img src={testimonial.logo} alt={`${testimonial.author} logo`} className="h-12 w-auto object-contain opacity-80" />
                      </div>}

                    <Quote className="w-10 h-10 text-primary mb-4 opacity-60" />

                    <blockquote className="text-foreground text-lg md:text-xl font-medium mb-6 leading-relaxed transition-transform duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-0.5">
                      "{testimonial.quote}"
                    </blockquote>

                    <div className="mt-auto transition-transform duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-0.5">
                      <p className="text-foreground font-semibold text-base md:text-lg">
                        {testimonial.author}
                      </p>
                      <p className="text-muted-foreground text-sm md:text-base">
                        {testimonial.position}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>)}
          </CarouselContent>

          <CarouselPrevious className="hidden md:flex" />
          <CarouselNext className="hidden md:flex" />
        </Carousel>
      </div>
    </section>;
}